(() => {
  const SECTION_SEL = '[data-bewo-sticker-explosion]';
  const BODY_ACTIVE = 'bewo-sticker-explosion-active';
  const BODY_LOCKED = 'bewo-sticker-explosion-locked';
  const BODY_IN = 'is-backdrop-in';
  const BODY_OUT = 'is-backdrop-out';

  const MAX_STICKERS_MOBILE = 44;
  const MAX_STICKERS_DESKTOP = 68;
  const GAP_FILL_RATIO = 0.28;
  const MIX_LOGO = 0.4;
  const MIX_DECOR = 0.4;
  const MIX_FLOWER = 0.2;
  const PRELOAD_TIMEOUT_MS = 900;

  let lockedScrollY = 0;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isDesktop = () => window.matchMedia('(min-width: 750px)').matches;

  const wait = (ms) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

  const parseConfig = (root) => {
    const configId = root.getAttribute('data-config-id');
    const el = configId ? document.getElementById(configId) : null;
    const raw = el ? el.textContent : root.getAttribute('data-config');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  };

  const buildMixedPool = (config, targetCount) => {
    const logos = config.logos?.length ? config.logos : config.stickers || [];
    const decor = config.decor || [];
    const accents = config.accents || [];
    let logoCount = Math.round(targetCount * MIX_LOGO);
    let decorCount = Math.round(targetCount * MIX_DECOR);
    let flowerCount = targetCount - logoCount - decorCount;

    if (!decor.length) {
      logoCount += decorCount;
      decorCount = 0;
    }

    if (!accents.length) {
      logoCount += flowerCount;
      flowerCount = 0;
    }

    const buckets = {
      logo: [],
      decor: [],
      accent: [],
    };
    let logoIndex = 0;
    let decorIndex = 0;
    let accentIndex = 0;

    for (let i = 0; i < logoCount; i += 1) {
      buckets.logo.push({ src: logos[logoIndex % logos.length], type: 'logo' });
      logoIndex += 1;
    }

    for (let i = 0; i < decorCount; i += 1) {
      buckets.decor.push({ src: decor[decorIndex % decor.length], type: 'decor' });
      decorIndex += 1;
    }

    for (let i = 0; i < flowerCount; i += 1) {
      buckets.accent.push({ src: accents[accentIndex % accents.length], type: 'accent' });
      accentIndex += 1;
    }

    const pattern = ['logo', 'decor', 'logo', 'decor', 'accent'];
    const pool = [];
    let patternIndex = 0;

    while (pool.length < targetCount) {
      let added = false;

      for (let step = 0; step < pattern.length && pool.length < targetCount; step += 1) {
        const bucketKey = pattern[patternIndex % pattern.length];
        patternIndex += 1;

        if (buckets[bucketKey].length) {
          pool.push(buckets[bucketKey].shift());
          added = true;
        }
      }

      if (added) continue;

      let drained = false;
      ['logo', 'decor', 'accent'].forEach((key) => {
        while (buckets[key].length && pool.length < targetCount) {
          pool.push(buckets[key].shift());
          drained = true;
        }
      });

      if (!drained) break;
    }

    return pool.slice(0, targetCount);
  };

  const getStickerSize = (type, ref, scale, desktop) => {
    const logoMin = desktop ? 148 : 120;
    const decorMin = desktop ? 96 : 78;
    const accentMin = desktop ? 72 : 58;

    if (type === 'logo') {
      return Math.max(ref * scale * 0.28, logoMin);
    }

    if (type === 'decor') {
      return Math.max(ref * scale * 0.2, decorMin);
    }

    if (type === 'accent') {
      return Math.max(ref * scale * 0.15, accentMin);
    }

    return Math.max(ref * scale * 0.16, desktop ? 96 : 78);
  };

  const computeGridSize = (aspect, desktop, config) => {
    const max = desktop ? MAX_STICKERS_DESKTOP : MAX_STICKERS_MOBILE;
    const gapBudget = Math.min(
      Math.round(max * GAP_FILL_RATIO),
      desktop ? 16 : 10
    );
    const mainTarget = max - gapBudget;
    let cols = desktop ? config.gridColsDesktop : config.gridColsMobile;
    let rows = Math.max(Math.ceil(mainTarget / cols), 1);

    while (cols * rows < mainTarget) {
      if (cols / rows < aspect) cols += 1;
      else rows += 1;
    }

    while (cols > 1 && (cols - 1) * rows >= mainTarget) cols -= 1;
    while (rows > 1 && cols * (rows - 1) >= mainTarget) rows -= 1;

    const mainSlots = cols * rows;
    const actualGap = Math.min(gapBudget, Math.max(cols - 1, 0) * Math.max(rows - 1, 0));

    return {
      cols,
      rows,
      mainSlots,
      gapBudget: actualGap,
      total: mainSlots + actualGap,
    };
  };

  const buildGridPositions = (items, padding, scale, desktop, layout) => {
    const padX = window.innerWidth * padding;
    const padY = window.innerHeight * padding;
    const width = Math.max(window.innerWidth - padX * 2, 1);
    const height = Math.max(window.innerHeight - padY * 2, 1);
    const ref = Math.min(window.innerWidth, window.innerHeight);
    const { cols, rows, mainSlots, gapBudget } = layout;
    const cellW = width / cols;
    const cellH = height / rows;
    const slots = [];

    for (let row = 0; row < rows; row += 1) {
      const brickOffset = row % 2 === 1 ? cellW * 0.5 : 0;

      for (let col = 0; col < cols; col += 1) {
        if (slots.length >= mainSlots) break;
        const jitterX = (Math.random() - 0.5) * cellW * 0.48;
        const jitterY = (Math.random() - 0.5) * cellH * 0.48;
        const x = padX + brickOffset + cellW * (col + 0.5) + jitterX;
        const y = padY + cellH * (row + 0.5) + jitterY;

        slots.push({
          x: Math.min(Math.max(x, padX), window.innerWidth - padX),
          y: Math.min(Math.max(y, padY), window.innerHeight - padY),
          isGap: false,
        });
      }
    }

    for (let row = 0; row < rows - 1; row += 1) {
      for (let col = 0; col < cols - 1; col += 1) {
        if (slots.length >= mainSlots + gapBudget) break;
        const jitterX = (Math.random() - 0.5) * cellW * 0.32;
        const jitterY = (Math.random() - 0.5) * cellH * 0.32;
        const x = padX + cellW * (col + 1) + jitterX;
        const y = padY + cellH * (row + 1) + jitterY;

        slots.push({
          x: Math.min(Math.max(x, padX), window.innerWidth - padX),
          y: Math.min(Math.max(y, padY), window.innerHeight - padY),
          isGap: true,
        });
      }
    }

    return items.map((item, index) => {
      const slot = slots[index] || slots[slots.length - 1];
      const isGap = item.isGap || slot.isGap;
      const stickerType = isGap && item.type === 'logo' ? 'logo' : item.type;
      const baseSize = getStickerSize(stickerType, ref, scale, desktop);
      const cellCap = Math.min(cellW, cellH) * (
        isGap && item.type === 'logo'
          ? 0.92
          : item.type === 'accent'
            ? 1.02
            : item.type === 'decor'
              ? 1.1
              : 1.18
      );
      const sizeJitter = isGap
        ? 0.88 + Math.random() * 0.16
        : item.type === 'accent'
          ? 0.9 + Math.random() * 0.14
          : 0.92 + Math.random() * 0.12;
      const size = Math.round(Math.min(baseSize, cellCap) * sizeJitter);

      return {
        x: slot.x,
        y: slot.y,
        size,
        radius: size * 0.48,
      };
    });
  };

  const clearNode = (node) => {
    if (node && node.parentNode) node.parentNode.removeChild(node);
  };

  const lockScroll = () => {
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.classList.add(BODY_LOCKED);
    document.body.style.top = `-${lockedScrollY}px`;
  };

  const unlockScroll = () => {
    document.body.classList.remove(BODY_LOCKED);
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
  };

  const showBackdrop = (fadeMs) => {
    document.body.style.setProperty('--bewo-se-backdrop-fade', `${fadeMs}ms`);
    document.body.classList.remove(BODY_OUT);
    document.body.classList.add(BODY_ACTIVE);
    lockScroll();
    window.requestAnimationFrame(() => {
      document.body.classList.add(BODY_IN);
    });
  };

  const hideBackdrop = (fadeMs, delayMs = 0) => {
    window.setTimeout(() => {
      document.body.classList.remove(BODY_IN);
      document.body.classList.add(BODY_OUT);
      window.setTimeout(() => {
        document.body.classList.remove(BODY_ACTIVE, BODY_OUT);
        document.body.style.removeProperty('--bewo-se-backdrop-fade');
        unlockScroll();
      }, fadeMs + 40);
    }, delayMs);
  };

  const preloadStickers = (pool) => {
    const urls = [...new Set(pool.map((item) => item.src).filter(Boolean))];

    const loads = urls.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.decoding = 'async';
          img.onload = () => resolve(src);
          img.onerror = () => resolve(src);
          img.src = src;
        })
    );

    return Promise.race([
      Promise.all(loads),
      wait(PRELOAD_TIMEOUT_MS),
    ]);
  };

  const createSticker = (item, pos, popMs, fadeMs) => {
    const img = document.createElement('img');
    img.className = 'bewo-sticker-explosion__sticker';
    if (item.type === 'logo') img.classList.add('is-logo');
    if (item.type === 'decor') img.classList.add('is-decor');
    if (item.type === 'accent') img.classList.add('is-accent');
    img.src = item.src;
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;

    const rot = Math.round((Math.random() * 24 - 12) * 10) / 10;
    img.style.setProperty('--bewo-se-rot', `${rot}deg`);
    img.style.setProperty('--bewo-se-size', `${pos.size}px`);
    img.style.setProperty('--bewo-se-pop', `${popMs}ms`);
    img.style.setProperty('--bewo-se-fade', `${fadeMs}ms`);
    img.style.left = `${pos.x}px`;
    img.style.top = `${pos.y}px`;
    img.loading = 'eager';

    return img;
  };

  const getBatchSize = (desktop, count) => {
    if (count <= 18) return desktop ? 5 : 4;
    return desktop ? 10 : 8;
  };

  const armStickers = (stickers) => {
    window.requestAnimationFrame(() => {
      stickers.forEach((img) => {
        img.classList.add('is-in');
      });
    });
  };

  const runSequence = (
    root,
    button,
    pool,
    positions,
    layer,
    fadeMs,
    holdMs,
    staggerMs,
    popMs
  ) =>
    new Promise((resolve) => {
      const reduced = prefersReducedMotion();
      const stickers = [];
      const desktop = isDesktop();
      const batchSize = getBatchSize(desktop, pool.length);
      const effectiveStagger = reduced ? Math.min(staggerMs, 70) : staggerMs;
      const batchCount = Math.ceil(pool.length / batchSize);

      pool.forEach((item, index) => {
        const pos = positions[index];
        const img = createSticker(item, pos, popMs, fadeMs);
        const batchIndex = Math.floor(index / batchSize);
        img.style.setProperty('--bewo-se-delay', `${batchIndex * effectiveStagger}ms`);
        layer.appendChild(img);
        stickers.push(img);
      });

      armStickers(stickers);

      const revealMs = (batchCount - 1) * effectiveStagger + popMs;
      const holdDelay = reduced ? 400 : holdMs;
      const finishDelay = revealMs + holdDelay + fadeMs + 80;

      window.setTimeout(() => {
        stickers.forEach((img) => {
          img.classList.remove('is-in', 'is-printing');
          img.classList.add('is-out');
        });
      }, revealMs + holdDelay);

      hideBackdrop(fadeMs, revealMs + holdDelay);

      window.setTimeout(() => {
        clearNode(layer);
        if (root._bewoSeLayer === layer) root._bewoSeLayer = null;
        root._bewoSeBusy = false;
        button.removeAttribute('aria-busy');
        resolve();
      }, finishDelay);
    });

  const explode = (root, button, config) => {
    if (root._bewoSeBusy) return;
    root._bewoSeBusy = true;
    button.setAttribute('aria-busy', 'true');

    clearNode(root._bewoSeLayer);
    document.body.classList.remove(BODY_ACTIVE, BODY_IN, BODY_OUT, BODY_LOCKED);
    document.body.style.top = '';

    const desktop = isDesktop();
    const logos = config.logos?.length ? config.logos : config.stickers || [];
    if (!logos.length && !config.stickers?.length) {
      root._bewoSeBusy = false;
      button.removeAttribute('aria-busy');
      return;
    }

    const scale = desktop ? config.scaleDesktop : config.scaleMobile;
    const staggerMs = desktop ? config.staggerMsDesktop : config.staggerMsMobile;
    const popMs = config.popMs || 680;
    const holdMs = config.holdMs;
    const fadeMs = config.fadeMs;
    const padding = config.gridPadding;

    const aspect = window.innerWidth / Math.max(window.innerHeight, 1);
    const layout = computeGridSize(aspect, desktop, config);
    const filledPool = buildMixedPool(config, layout.total);
    const positions = buildGridPositions(filledPool, padding, scale, desktop, layout);

    showBackdrop(fadeMs);

    const layer = document.createElement('div');
    layer.className = 'bewo-sticker-explosion__layer';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);
    root._bewoSeLayer = layer;

    preloadStickers(filledPool);
    runSequence(
      root,
      button,
      filledPool,
      positions,
      layer,
      fadeMs,
      holdMs,
      staggerMs,
      popMs
    );
  };

  const init = (root) => {
    if (root._bewoSeInit) return;
    root._bewoSeInit = true;

    if (root.parentElement !== document.body) {
      document.body.appendChild(root);
    }

    const config = parseConfig(root);
    const button = root.querySelector('[data-bewo-sticker-explosion-btn]');
    if (!config || !button) return;

    button.addEventListener('click', () => {
      explode(root, button, config);
    });
  };

  const boot = () => {
    document.querySelectorAll(SECTION_SEL).forEach(init);
  };

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.(SECTION_SEL) || event.target.closest?.(SECTION_SEL);
    if (root) init(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
